import styled, { css } from 'styled-components/macro'

import {
  Accordion,
  Button,
  Divider,
  Layout,
  Link,
  ProgressBarCountdown,
  Text,
} from '@loadsmart/miranda-react'
import { getToken, toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { getDelivery, getPickup } from 'core/domain/Stop'
import { formatAddress, formatDescription, formatZip } from 'core/presenters/stopPresenter'
import { ShippingItems } from 'core/shipping-items/ui/ShippingItems'
import { Icon } from 'core/ui/components/icon/Icon'
import { Logo } from 'core/ui/components/logo/Logo'
import { StopsTimeline } from 'core/ui/components/stop/StopsTimeline'
import { formatCurrency } from 'core/utils/currency'

import { screen } from 'legacy/styles/screen'

import {
  formatPickupDate,
  formatDeliveryDate,
  formatTimeRemainingString,
  formatEquipmentType,
  formatWeight,
  getModeIconName,
} from '../presenters/tenderListCardPresenter'
import { useTenderExpiration } from './hooks/useTenderExpiration'
import { type TenderCarrier, type TenderOffer } from './types'

export const MoreInfoSectionWrapper = styled(Layout.Group)`
  flex-wrap: wrap;

  ${screen.md} {
    flex-wrap: unset;
  }

  gap: ${getToken('spacing-6')};
`

export const ColumnWrapper = styled(Layout.Stack)`
  flex-basis: 100%;

  ${screen.md} {
    flex-basis: 33.3%;
  }
`

export const StyledLayout = styled.div<{
  $highlighted?: boolean
}>`
  background-color: ${toCSSValue('color-background-primary')};
  border: ${getToken('border-thin')} solid ${toCSSValue('color-border')};
  border-radius: ${getToken('border-radius-s')};
  margin-bottom: ${getToken('spacing-4')};
  overflow: hidden;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      border-color: ${toCSSValue('color-primary-60')};
    `}
`

const HeaderSection = styled(Layout.Group)`
  padding: ${getToken('spacing-2')} ${getToken('spacing-4')};
  border-bottom: ${getToken('border-thin')} solid ${toCSSValue('color-border')};
  background: ${toCSSValue('color-background-secondary')};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HorizontalDivider = styled.div`
  height: 2px;
  width: 32px;
  background-color: ${toCSSValue('color-border')};
  flex-shrink: 0;
`

const ExpirationContent = styled(Layout.Group)`
  align-items: center;
  gap: ${getToken('spacing-2')};

  [role='progressbar'] {
    flex-grow: 1;
  }
`

const DEFAULT_MAX_PROGRESS_BAR = 100

const StyledText = styled(Text)`
  :after {
    content: ' • ';
  }

  :last-child:after {
    content: none;
  }
`

const NotesList = styled.ul`
  margin: 0;
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: ${getToken('spacing-1')};
`

const AttachmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding: ${getToken('spacing-4')};
  border: 1px solid ${toCSSValue('color-border')};
  background-color: ${toCSSValue('color-background-primary')};
  border-radius: ${getToken('border-radius-s')};
`

export type TenderDetailsProps = Readonly<{
  id: string
  tender: TenderOffer
  carrier?: TenderCarrier
  disabled?: boolean
  onAcceptClick: () => void
  onRejectClick: () => void
  highlighted?: boolean
}>

export function TenderDetails({
  id,
  tender,
  carrier,
  disabled = false,
  onAcceptClick,
  onRejectClick,
  highlighted,
}: TenderDetailsProps) {
  const { timeRemaining, progressValue } = useTenderExpiration(tender.expirationDate)
  const { hoursString, minutesString } = formatTimeRemainingString(timeRemaining)

  const pickup = getPickup(tender.stops)
  const delivery = getDelivery(tender.stops)

  if (!pickup || !delivery) {
    return null
  }

  const pickupDateFormatted = formatPickupDate(pickup)
  const deliveryDateFormatted = formatDeliveryDate(delivery)

  const totalWeight = tender.measures?.totalWeight

  return (
    <StyledLayout $highlighted={highlighted} data-testid="tender-list-card">
      <Layout.Stack justify="center">
        <HeaderSection>
          <Layout.Group gap="spacing-1" align="center">
            {tender.shipper.logoUrl && <Logo url={tender.shipper.logoUrl} />}
            <Text variant="body-md" color="color-text-secondary">
              {tender.shipper.companyName}
            </Text>
          </Layout.Group>
          <Layout.Group gap="spacing-0-5">
            <Text variant="body-md">{tender.carrier.email}</Text>
            {carrier?.mc && (
              <>
                <Text variant="body-md" color="color-text-secondary-inverted">
                  &bull;
                </Text>
                <Text variant="body-md">MC {carrier.mc}</Text>
              </>
            )}
            {carrier?.dot && (
              <>
                <Text variant="body-md" color="color-text-secondary-inverted">
                  &bull;
                </Text>
                <Text variant="body-md">DOT {carrier.dot}</Text>
              </>
            )}
          </Layout.Group>
        </HeaderSection>

        <Layout.Group
          style={{ display: 'flex' }}
          paddingX="spacing-4"
          paddingY="spacing-6"
          gap="spacing-2"
          justify="space-between"
          align="center"
        >
          <Layout.Group align="flex-start" justify="flex-start">
            <Layout.Group>
              <Layout.Stack gap="spacing-0-5">
                <Text variant="body-md-bold">
                  {pickup.city}, {pickup.state}
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  {pickupDateFormatted.dateText}
                  {pickupDateFormatted.statusText && (
                    <>
                      {' • '}
                      {pickupDateFormatted.isPendingSchedule ? (
                        <Text color="color-warning-100">{pickupDateFormatted.statusText}</Text>
                      ) : (
                        pickupDateFormatted.statusText
                      )}
                      {pickupDateFormatted.timeText && ` ${pickupDateFormatted.timeText}`}
                    </>
                  )}
                </Text>
              </Layout.Stack>
            </Layout.Group>

            <Layout.Group align="center" gap="spacing-2" style={{ flexShrink: '0' }}>
              <HorizontalDivider />
              <Text variant="body-sm" color="color-text-secondary">
                {tender.mileage} mi
              </Text>
              <HorizontalDivider />
            </Layout.Group>

            <Layout.Group>
              <Layout.Stack gap="spacing-0-5">
                <Text variant="body-md-bold">
                  {delivery.city}, {delivery.state}
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  {deliveryDateFormatted.dateText}
                  {deliveryDateFormatted.statusText && (
                    <>
                      {' • '}
                      {deliveryDateFormatted.isPendingSchedule ? (
                        <Text color="color-warning-100">{deliveryDateFormatted.statusText}</Text>
                      ) : (
                        deliveryDateFormatted.statusText
                      )}
                      {deliveryDateFormatted.timeText && ` ${deliveryDateFormatted.timeText}`}
                    </>
                  )}
                </Text>
              </Layout.Stack>
            </Layout.Group>
          </Layout.Group>
          <Layout.Stack>
            <Layout.Stack align="flex-end" gap="spacing-1">
              <Layout.Group gap="spacing-0-5" align="center">
                <Text variant="body-sm-bold" color="color-primary-100">
                  $
                </Text>
                <Text variant="heading-md-bold">{formatCurrency(tender.flatRate, false, '')}</Text>
              </Layout.Group>

              <Text variant="body-sm">
                per mile <Text variant="body-sm">{formatCurrency(tender.pricePerMile, true)}</Text>
              </Text>
            </Layout.Stack>
            <Layout.Group justify="flex-end" gap="spacing-3">
              <Button variant="secondary" onClick={onRejectClick} disabled={disabled}>
                Reject
              </Button>
              <Button variant="primary" onClick={onAcceptClick} disabled={disabled}>
                Accept
              </Button>
            </Layout.Group>
          </Layout.Stack>
        </Layout.Group>
        <Layout.Box paddingX="spacing-6" paddingY="spacing-3">
          <ExpirationContent>
            <Text variant="body-md">
              Offer expires in{' '}
              <Text variant="body-md-bold">
                {hoursString}h{minutesString}m
              </Text>
            </Text>
            <ProgressBarCountdown max={DEFAULT_MAX_PROGRESS_BAR} value={progressValue} />
          </ExpirationContent>
        </Layout.Box>
        <Layout.Stack gap="spacing-2" paddingX="spacing-6" paddingY="spacing-3">
          <Accordion style={{ backgroundColor: toCSSValue('color-background-secondary') }}>
            <Accordion.Title>
              <Layout.Group gap="spacing-2" align="center">
                <Icon name={getModeIconName(tender.mode)} size="24px" />
                <Text variant="body-md">
                  {tender.mode.toUpperCase()} &bull;{' '}
                  {tender.equipmentTypeLabel || formatEquipmentType(tender.equipmentType)}
                  {totalWeight ? ` • ${formatWeight(totalWeight)}` : ''}
                </Text>
              </Layout.Group>
            </Accordion.Title>
            <Accordion.Content>
              <Divider
                style={{
                  '--m-divider-margin-bottom': `${getToken('spacing-8')}`,
                }}
              />
              <MoreInfoSectionWrapper gap="spacing-1">
                <ColumnWrapper>
                  <Layout.Stack gap="spacing-4">
                    <StopsTimeline
                      stops={tender.stops}
                      titleFormatter={formatDescription}
                      superTitleFormatter={formatAddress}
                      subTitleFormatter={formatZip}
                      tags
                    />
                  </Layout.Stack>
                </ColumnWrapper>
                <ColumnWrapper gap="spacing-6">
                  {(tender.shipper.name || tender.shipper.email || tender.shipper.phoneNumber) && (
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Shipper contact
                      </Text>
                      <Layout.Group gap="spacing-1">
                        {tender.shipper.name && (
                          <StyledText color="color-text-tertiary">{tender.shipper.name}</StyledText>
                        )}
                        {tender.shipper.email && (
                          <StyledText color="color-text-tertiary">
                            {tender.shipper.email}
                          </StyledText>
                        )}
                        {tender.shipper.phoneNumber && (
                          <StyledText color="color-text-tertiary">
                            {tender.shipper.phoneNumber}
                          </StyledText>
                        )}
                      </Layout.Group>
                      <Button variant="icon">
                        <Icon name="chat" color="success-dark" size="16px" />
                      </Button>
                    </Layout.Stack>
                  )}

                  <Layout.Group align="center">
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Mode
                      </Text>
                      <Text variant="body-md" color="color-text-tertiary">
                        {tender.mode}
                      </Text>
                    </Layout.Stack>
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Equipment type
                      </Text>
                      <Text variant="body-md" color="color-text-tertiary">
                        {tender.equipmentTypeLabel || formatEquipmentType(tender.equipmentType)}
                      </Text>
                    </Layout.Stack>
                    {tender.shipperGuideId && (
                      <Layout.Stack gap="spacing-0-5">
                        <Text variant="body-md-bold" color="color-text-primary">
                          ShipperGuide ID
                        </Text>
                        <Text variant="body-md" color="color-text-tertiary">
                          {tender.shipperGuideId}
                        </Text>
                      </Layout.Stack>
                    )}
                  </Layout.Group>

                  <Layout.Stack gap="spacing-0-5">
                    <Text variant="body-md-bold" color="color-text-primary">
                      Accessorial charges
                    </Text>
                    <Text variant="body-sm" color="color-text-tertiary">
                      No accessorial charges
                    </Text>
                  </Layout.Stack>

                  {tender.notes && (
                    <Layout.Stack gap="spacing-0-5">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Shipper notes
                      </Text>
                      <NotesList>
                        <li>
                          <Text variant="body-sm" color="color-text-secondary">
                            {tender.notes}
                          </Text>
                        </li>
                      </NotesList>
                    </Layout.Stack>
                  )}

                  {tender.attachments && tender.attachments.length > 0 && (
                    <Layout.Stack gap="spacing-2">
                      <Text variant="body-md-bold" color="color-text-primary">
                        Attachments
                      </Text>
                      {tender.attachments.map(attachment => (
                        <AttachmentItem key={attachment.id}>
                          <Text variant="body-sm">{attachment.fileName}</Text>
                          <Link href={attachment.url} target="_blank" size="small">
                            View
                          </Link>
                        </AttachmentItem>
                      ))}
                    </Layout.Stack>
                  )}
                </ColumnWrapper>
                <ColumnWrapper>
                  {tender.items && tender.items.length > 0 && (
                    <ShippingItems measures={tender.measures} shippingItems={tender.items} />
                  )}
                </ColumnWrapper>
              </MoreInfoSectionWrapper>
            </Accordion.Content>
          </Accordion>
        </Layout.Stack>
      </Layout.Stack>
    </StyledLayout>
  )
}
